import { TableShell } from '../../components/molecules/TableShell'
import { Alert } from '../../components/atoms/Alert'
import { CheckMark } from '../../components/atoms/CheckMark'
import { Spinner } from '../../components/atoms/Spinner'
import { Th, Td } from '../../components/molecules/Table'
import { Paginator } from '../../components/molecules/Paginator'
import { ACTIONFLAGS, ACTIONLABELS } from '../../../constants/action.constants'
import {
  useScreenActions,
  type ScreenWithActions
} from '../../../viewmodels/useScreenActions'

/**
 * /iam/screen-actions — read-only catalogue of which actions each
 * screen exposes.
 *
 * The flags mirror what each screen's handler actually implements;
 * they are authored in code + migrations, not edited here. Table
 * view: rows = screens; columns = the 5 action flags shown as a
 * forest tick when present (same indicator as the Variant Pricing
 * "Cur" column).
 */
export function IamScreenActionsPage(): JSX.Element {
  const { items, total, page, pageSize, isLoading, error, setPage, reload } =
    useScreenActions()

  return (
    <div className="space-y-6">
      <header>
        <h1 className="font-heading text-h2 text-charcoal">Screen Actions</h1>
      </header>

      {error !== null ? (
        <Alert tone="error">
          {error}{' '}
          <button
            type="button"
            className="underline underline-offset-2 hover:text-charcoal"
            onClick={() => void reload()}
          >
            Retry
          </button>
        </Alert>
      ) : null}

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Spinner />
        </div>
      ) : items.length === 0 ? (
        <p className="font-body text-body-sm text-text-secondary">
          No screens registered.
        </p>
      ) : (
        <>
          <ScreenActionsTable items={items} />
          <Paginator
            page={page}
            pageSize={pageSize}
            total={total}
            onPageChange={setPage}
            disabled={isLoading}
          />
        </>
      )}
    </div>
  )
}

function ScreenActionsTable({
  items
}: {
  readonly items: ReadonlyArray<ScreenWithActions>
}): JSX.Element {
  return (
    <TableShell>
      <table className="w-full font-body text-body-sm">
        <thead className="bg-parchment/50 text-text-secondary">
          <tr>
            <Th>Name</Th>
            {ACTIONFLAGS.map((flag) => (
              <Th key={flag} className="text-center">
                {ACTIONLABELS[flag]}
              </Th>
            ))}
          </tr>
        </thead>
        <tbody>
          {items.map((row) => (
            <tr key={row.id} className="border-t border-border">
              <Td>
                <span className="font-body text-body-sm text-charcoal">{row.name}</span>
              </Td>
              {ACTIONFLAGS.map((flag) => (
                <Td key={flag} className="text-center">
                  <CheckMark
                    on={row[flag] === 1}
                    label={`${ACTIONLABELS[flag]} on ${row.name}`}
                  />
                </Td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </TableShell>
  )
}
